import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { ThemedText, SubmissionsChart, SkeletonStats, ErrorState } from '../components';
import { Layout } from '../components/Layout';
import { dashboardService, systemStatusService } from '../api';
import usageService from '../api/services/usageService';
import { extractItems } from '../utils/responseUtils';
import { getWorkOrderStatusLabel } from '../api/services/workOrderService';
import { formatDateOnly } from '../utils/dateUtils';
import { useRole } from '../hooks/useRole';
import { ChunkErrorBoundary } from '../components/ChunkErrorBoundary';
import { WorkOrderStatusColors as STATUS_COLORS, StatusPalette, SentryLevelColors, withOpacity } from '../constants/statusColors';
import {
  ClipboardList, BarChart3, Users, ClipboardCheck,
  Wrench, FileStack, Calendar, User, FileText,
  AlertTriangle, Globe, MapPin,
} from 'lucide-react';

// --- Styled Components ---

const WelcomeBar = styled.div`
  background: ${props => props.theme.isDark
    ? `linear-gradient(135deg, ${props.theme.colors.tint}22, ${props.theme.colors.tint}08)`
    : 'linear-gradient(135deg, #232562 0%, #2d3070 50%, #3a3f8f 100%)'};
  color: white;
  padding: ${props => props.theme.spacing.xl} ${props => props.theme.spacing.xl};
  border-radius: ${props => props.theme.borderRadius.xl || '16px'};
  margin-bottom: ${props => props.theme.spacing.lg};
  position: relative;
  overflow: hidden;
  min-height: 140px;
  display: flex;
  align-items: center;

  /* Large decorative orb */
  &::before {
    content: '';
    position: absolute;
    top: -80px;
    right: -80px;
    width: 320px;
    height: 320px;
    background: radial-gradient(circle, rgba(89, 202, 238, 0.12) 0%, transparent 70%);
    border-radius: 50%;
    pointer-events: none;
  }

  /* Subtle secondary orb */
  &::after {
    content: '';
    position: absolute;
    bottom: -60px;
    right: 80px;
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, rgba(89, 202, 238, 0.06) 0%, transparent 70%);
    border-radius: 50%;
    pointer-events: none;
  }
`;

const WelcomeLeft = styled.div`
  position: relative;
  z-index: 1;
  max-width: 600px;
`;

const WelcomeDecor = styled.div`
  position: absolute;
  right: 32px;
  bottom: 0;
  opacity: 0.35;
  pointer-events: none;
  z-index: 0;
  color: white;
  display: flex;
  align-items: flex-end;
`;

const MaterialMonitoringIcon = () => (
  <span
    className="material-symbols-outlined"
    style={{
      fontSize: '120px',
      fontVariationSettings: "'FILL' 1",
    }}
  >
    monitoring
  </span>
);

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.lg};

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const MiniStatCard = styled.div`
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border-radius: ${props => props.theme.borderRadius.lg || '12px'};
  padding: 20px;
  border: 1px solid ${props => props.theme.colors.border};
  display: flex;
  align-items: center;
  gap: 14px;
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  ${props => props.clickable && `
    &:hover {
      transform: translateY(-1px);
      box-shadow: ${props.theme?.shadows?.md};
    }
  `}
`;

const MiniStatIcon = styled.div`
  font-size: 24px;
  width: 40px;
  height: 40px;
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.bg || props.theme.colors.tint + '18'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const MiniStatContent = styled.div`
  min-width: 0;
`;

const MiniStatValue = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  line-height: 1.2;
`;

const MiniStatLabel = styled.div`
  font-size: 12px;
  color: ${props => props.theme.colors.muted};
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-top: 2px;
`;

const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.lg};

  @media (max-width: 1000px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  background-color: ${props => props.theme.isDark ? props.theme.colors.backgroundSecondary : '#f2f3ff'};
  border-radius: ${props => props.theme.borderRadius.xl || '16px'};
  overflow: hidden;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
`;

const PanelTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PanelAccent = styled.div`
  width: 4px;
  height: 28px;
  border-radius: 4px;
  background-color: ${props => props.$color || props.theme.colors.tint};
`;

const PanelTitle = styled.div`
  font-family: 'Manrope', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
`;

const PanelLink = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary || '#232562'};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const PanelBody = styled.div`
  padding: ${props => props.noPadding ? '0 24px 24px' : '24px'};
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

// --- Work Order list items ---
const WOItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  border-bottom: 1px solid ${props => props.theme.colors.border};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${props => props.theme.colors.background};
  }
`;

const WOStatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => props.color};
  flex-shrink: 0;
`;

const WOInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const WOTitle = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${props => props.theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const WOMeta = styled.div`
  font-size: 11px;
  color: ${props => props.theme.colors.muted};
  margin-top: 2px;
  display: flex;
  gap: 8px;
`;

const WOBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  padding: 1px 7px;
  border-radius: 10px;
  color: white;
  background-color: ${props => props.color};
  flex-shrink: 0;
`;

// --- Recent submissions ---
const SubItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border-radius: 10px;
`;

const SubIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 7px;
  background-color: ${props => props.theme.colors.tint + '12'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
`;

const SubInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const SubTitle = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${props => props.theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SubMeta = styled.div`
  font-size: 11px;
  color: ${props => props.theme.colors.muted};
  margin-top: 2px;
`;

// --- Quick actions row ---
const QuickActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  padding: 0 24px 24px;
  flex: 1;
  align-content: start;
  padding-top: 24px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const QuickActionCard = styled.button`
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border}60;
  border-radius: 14px;
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(35, 37, 98, 0.08);
    border-color: ${props => props.theme.colors.tint}40;
  }
`;

const QuickActionIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background-color: ${props => props.$bg || '#59caee18'};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const QuickActionLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: inherit;
`;

const QuickActionDesc = styled.div`
  font-size: 11px;
  color: ${props => props.theme.colors.muted};
  line-height: 1.3;
`;

const TimezoneInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 24px 20px;
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 13px;
  background-color: ${props => props.$mismatch
    ? '#fef3c720'
    : props.theme.colors.backgroundSecondary};
  color: ${props => props.$mismatch ? '#d97706' : props.theme.colors.muted};
  border: 1px solid ${props => props.$mismatch ? '#d9760620' : props.theme.colors.border + '60'};
`;

const QuickActionsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${props => props.theme.spacing.md};
`;

const QuickActionBtn = styled.button`
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;
  color: ${props => props.theme.colors.text};

  &:hover {
    border-color: ${props => props.theme.colors.tint};
    transform: translateY(-1px);
    box-shadow: ${props => props.theme?.shadows?.md};
  }
`;

const QAIcon = styled.div`
  font-size: 20px;
  width: 36px;
  height: 36px;
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.bg || props.theme.colors.tint + '15'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const QAText = styled.div`
  min-width: 0;
`;

const QATitle = styled.div`
  font-size: 13px;
  font-weight: 600;
`;

const QADesc = styled.div`
  font-size: 11px;
  color: ${props => props.theme.colors.muted};
  margin-top: 2px;
`;

const EmptyState = styled.div`
  padding: 24px 16px;
  text-align: center;
  font-size: 13px;
  color: ${props => props.theme.colors.muted};
`;

const LoadingText = styled.div`
  padding: 24px 16px;
  text-align: center;
  font-size: 13px;
  color: ${props => props.theme.colors.muted};
`;

// --- License Status Card ---
const EnterpriseRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.lg};

  @media (max-width: 1000px) {
    grid-template-columns: 1fr;
  }
`;

const LeftStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
  min-height: 0;
`;

const ScrollablePanel = styled(Panel)`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ScrollablePanelBody = styled.div`
  margin: 0 24px 24px;
  padding: 10px;
  max-height: 220px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: ${props => props.theme.isDark ? props.theme.colors.background : '#eaecf8'};
  border-radius: 12px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.border};
    border-radius: 4px;
  }
`;

const LicenseCard = styled.div`
  background-color: ${props => props.theme.isDark ? props.theme.colors.backgroundSecondary : '#f2f3ff'};
  border-radius: ${props => props.theme.borderRadius.xl || '16px'};
  padding: 0;
  overflow: hidden;
`;

const LicenseCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
`;

const LicenseCardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LicenseBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  color: white;
  background-color: ${props => props.color || StatusPalette.green};
`;

const LicenseMetrics = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 0 24px 24px;
`;

const LicenseMetric = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LicenseMetricLabel = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.theme.colors.text};
  width: 120px;
  flex-shrink: 0;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const LicenseMetricBar = styled.div`
  flex: 1;
  height: 8px;
  border-radius: 4px;
  background-color: ${props => props.theme.colors.border}60;
  overflow: hidden;
`;

const LicenseMetricFill = styled.div`
  height: 100%;
  border-radius: 4px;
  width: ${props => Math.min(props.percent, 100)}%;
  background-color: ${props =>
    props.percent > 95 ? StatusPalette.red :
    props.percent > 80 ? StatusPalette.amber :
    props.$color || '#232562'};
  transition: width 0.3s ease;
`;

const LicenseMetricValue = styled.div`
  font-size: 12px;
  color: ${props => props.theme.colors.muted};
  font-weight: 500;
  min-width: 70px;
  text-align: right;
  flex-shrink: 0;
`;

const LicenseWarning = styled.div`
  margin: 0 24px 16px;
  padding: 8px 12px;
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.severe ? withOpacity(StatusPalette.red, '15') : withOpacity(StatusPalette.amber, '15')};
  border: 1px solid ${props => props.severe ? withOpacity(StatusPalette.red, '30') : withOpacity(StatusPalette.amber, '30')};
  font-size: 12px;
  color: ${props => props.severe ? StatusPalette.red : StatusPalette.amber};
  font-weight: 500;
`;

const SectionTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.md};
`;

// --- Timezone Banner ---
const TimezoneBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: ${props => props.theme.spacing.md};
  font-size: 13px;
  background-color: ${props => props.$mismatch
    ? (props.theme.isDark ? '#f59e0b15' : '#fef3c7')
    : props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.$mismatch
    ? (props.theme.isDark ? '#f59e0b30' : '#fde68a')
    : props.theme.colors.border};
  color: ${props => props.theme.colors.text};
`;

const TimezoneBannerIcon = styled.span`
  font-size: 16px;
  flex-shrink: 0;
`;

const TimezoneBannerText = styled.div`
  flex: 1;
  min-width: 0;
`;

const TimezoneBannerLabel = styled.span`
  color: ${props => props.theme.colors.muted};
`;

const TimezoneBannerLink = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.tint};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  white-space: nowrap;
  &:hover { text-decoration: underline; }
`;

// --- Helpers ---
const formatTimeAgo = (dateStr) => {
  if (!dateStr) return 'N/A';
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

// --- Sentry Issues Widget ---
const SentryIssueItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-bottom: 1px solid ${props => props.theme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const IssueLevelDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => SentryLevelColors[props.$level] || StatusPalette.blue};
  flex-shrink: 0;
`;

const IssueInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const IssueTitle = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${props => props.theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const IssueMeta = styled.div`
  font-size: 11px;
  color: ${props => props.theme.colors.muted};
  margin-top: 2px;
  display: flex;
  gap: 8px;
`;

const LevelBadgeSmall = styled.span`
  font-size: 10px;
  font-weight: 600;
  padding: 1px 7px;
  border-radius: 10px;
  background-color: ${props => withOpacity(SentryLevelColors[props.$level] || StatusPalette.blue)};
  color: ${props => SentryLevelColors[props.$level] || StatusPalette.blue};
  flex-shrink: 0;
`;

// --- Component ---

// Lazy import to avoid circular deps
const SuperAdminDashboardLazy = React.lazy(() =>
  import('./SuperAdminDashboardPage').then(m => ({ default: m.SuperAdminDashboardPage }))
);

export const DashboardPage = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isSuperAdmin } = useRole();
  const superAdmin = isSuperAdmin();

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [upcomingWorkOrders, setUpcomingWorkOrders] = useState([]);
  const [workOrdersLoading, setWorkOrdersLoading] = useState(true);
  const [licenseUsage, setLicenseUsage] = useState(null);
  const [sentryIssues, setSentryIssues] = useState([]);
  const [sentryLoading, setSentryLoading] = useState(false);

  // Timezone detection
  const browserTimezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const userTimezone = user?.timezoneId;
  const timezoneMismatch = userTimezone && browserTimezone && userTimezone !== browserTimezone;

  useEffect(() => {
    if (superAdmin) return;

    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setFetchError(null);
        const data = await dashboardService.getDashboardSummary();
        setSummary(data);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
        setFetchError(error?.response?.data?.message || 'Failed to load dashboard. Please check your connection and try again.');
        toast.error('Failed to load dashboard statistics');
      } finally {
        setLoading(false);
      }
    };

    const fetchAnalytics = async () => {
      try {
        setAnalyticsLoading(true);
        const data = await dashboardService.getAnalytics(12);
        setAnalytics(data);
      } catch (error) {
        console.error('Failed to fetch analytics data:', error);
      } finally {
        setAnalyticsLoading(false);
      }
    };

    const fetchUpcomingWorkOrders = async () => {
      try {
        setWorkOrdersLoading(true);
        const response = await dashboardService.getUpcomingWorkOrders(14);
        setUpcomingWorkOrders(extractItems(response));
      } catch (error) {
        console.error('Failed to fetch upcoming work orders:', error);
      } finally {
        setWorkOrdersLoading(false);
      }
    };

    const fetchLicenseUsage = async () => {
      try {
        const data = await usageService.getMyUsage();
        setLicenseUsage(data);
      } catch (error) {
        // Non-critical - don't show toast, just log
        console.error('Failed to fetch license usage:', error);
      }
    };

    fetchDashboardData();
    fetchAnalytics();
    fetchUpcomingWorkOrders();
    fetchLicenseUsage();
  }, [superAdmin, user?.id]);

  // SuperAdmin gets a dedicated dashboard
  if (superAdmin) {
    return (
      <Layout currentPath="/dashboard">
        <ChunkErrorBoundary>
          <React.Suspense fallback={<div style={{ padding: 24, textAlign: 'center', color: theme.colors.muted }}>Loading...</div>}>
            <SuperAdminDashboardLazy />
          </React.Suspense>
        </ChunkErrorBoundary>
      </Layout>
    );
  }

  // Format recent submissions from summary
  const recentSubmissions = summary?.recentSubmissions?.$values || summary?.recentSubmissions || [];

  return (
    <Layout currentPath="/dashboard">
      {/* Welcome bar - compact */}
      <WelcomeBar theme={theme}>
        <WelcomeLeft>
          <div style={{ fontFamily: "'Manrope', sans-serif", fontSize: '2rem', fontWeight: 800, marginBottom: 8, lineHeight: 1.2 }}>
            Welcome Back, {user?.fullName || 'User'}
          </div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '1rem', opacity: 0.8, lineHeight: 1.6 }}>
            Here's what's happening with your projects today.
          </div>
        </WelcomeLeft>
        <WelcomeDecor>
          <MaterialMonitoringIcon />
        </WelcomeDecor>
      </WelcomeBar>

      {/* Error state */}
      {fetchError && !loading && (
        <ErrorState
          theme={theme}
          title="Failed to load dashboard"
          message={fetchError}
          onRetry={() => window.location.reload()}
        />
      )}

      {/* Stats row — full width */}
      {loading && <SkeletonStats count={4} theme={theme} />}
      <StatsRow theme={theme} style={{ display: loading || fetchError ? 'none' : undefined }}>
        <MiniStatCard theme={theme} clickable onClick={() => navigate('/forms')}>
          <MiniStatIcon theme={theme} bg={theme.colors.tint + '18'}>
            <ClipboardList size={22} color={theme.colors.tint} />
          </MiniStatIcon>
          <MiniStatContent>
            <MiniStatValue theme={theme}>{loading ? '-' : (summary?.totalForms || 0)}</MiniStatValue>
            <MiniStatLabel theme={theme}>Total Forms</MiniStatLabel>
          </MiniStatContent>
        </MiniStatCard>

        <MiniStatCard theme={theme}>
          <MiniStatIcon theme={theme} bg={withOpacity(StatusPalette.green, '18')}>
            <BarChart3 size={22} color={StatusPalette.green} />
          </MiniStatIcon>
          <MiniStatContent>
            <MiniStatValue theme={theme}>{loading ? '-' : (summary?.totalSubmissions || 0)}</MiniStatValue>
            <MiniStatLabel theme={theme}>Form Responses</MiniStatLabel>
          </MiniStatContent>
        </MiniStatCard>

        <MiniStatCard theme={theme} clickable onClick={() => navigate('/users')}>
          <MiniStatIcon theme={theme} bg={withOpacity(StatusPalette.amber, '18')}>
            <Users size={22} color={StatusPalette.amber} />
          </MiniStatIcon>
          <MiniStatContent>
            <MiniStatValue theme={theme}>{loading ? '-' : (summary?.activeUsers || 0)}</MiniStatValue>
            <MiniStatLabel theme={theme}>Active Users</MiniStatLabel>
          </MiniStatContent>
        </MiniStatCard>

        <MiniStatCard theme={theme} clickable onClick={() => navigate('/work-orders')}>
          <MiniStatIcon theme={theme} bg={withOpacity(StatusPalette.purple, '18')}>
            <ClipboardCheck size={22} color={StatusPalette.purple} />
          </MiniStatIcon>
          <MiniStatContent>
            <MiniStatValue theme={theme}>{workOrdersLoading ? '-' : upcomingWorkOrders.length}</MiniStatValue>
            <MiniStatLabel theme={theme}>Upcoming WOs</MiniStatLabel>
          </MiniStatContent>
        </MiniStatCard>
      </StatsRow>

      {/* Enterprise Status + Submission Trends — side by side */}
      {/* Enterprise Status + Recent Submissions (left) | Submission Trends (right) */}
      <EnterpriseRow theme={theme}>
        {/* Left column: stacked */}
        <LeftStack theme={theme}>
          {/* Enterprise Status */}
          {licenseUsage && (
            <LicenseCard theme={theme}>
              <LicenseCardHeader>
                <LicenseCardTitle>
                  <PanelAccent $color="#232562" />
                  <PanelTitle theme={theme}>Enterprise Status</PanelTitle>
                </LicenseCardTitle>
                {licenseUsage.licenseStatus && (
                  <LicenseBadge color={
                    licenseUsage.licenseStatus === 'Active' ? StatusPalette.green :
                    licenseUsage.licenseStatus === 'Trial' ? StatusPalette.blue :
                    licenseUsage.licenseStatus === 'GracePeriod' ? StatusPalette.amber :
                    StatusPalette.red
                  }>
                    {licenseUsage.licenseStatus}
                  </LicenseBadge>
                )}
              </LicenseCardHeader>
              <LicenseMetrics>
                {licenseUsage.submissionsThisMonth && licenseUsage.submissionsThisMonth.limit !== null && (
                  <LicenseMetric>
                    <LicenseMetricLabel theme={theme}>Submissions</LicenseMetricLabel>
                    <LicenseMetricBar theme={theme}>
                      <LicenseMetricFill percent={licenseUsage.submissionsThisMonth.percentage || 0} $color="#232562" />
                    </LicenseMetricBar>
                    <LicenseMetricValue theme={theme}>
                      {licenseUsage.submissionsThisMonth.current} / {licenseUsage.submissionsThisMonth.limit}
                    </LicenseMetricValue>
                  </LicenseMetric>
                )}
                {licenseUsage.users && licenseUsage.users.limit !== null && (
                  <LicenseMetric>
                    <LicenseMetricLabel theme={theme}>Users</LicenseMetricLabel>
                    <LicenseMetricBar theme={theme}>
                      <LicenseMetricFill percent={licenseUsage.users.percentage || 0} $color="#232562" />
                    </LicenseMetricBar>
                    <LicenseMetricValue theme={theme}>
                      {licenseUsage.users.current} / {licenseUsage.users.limit}
                    </LicenseMetricValue>
                  </LicenseMetric>
                )}
                {licenseUsage.formSchemas && licenseUsage.formSchemas.limit !== null && (
                  <LicenseMetric>
                    <LicenseMetricLabel theme={theme}>Forms</LicenseMetricLabel>
                    <LicenseMetricBar theme={theme}>
                      <LicenseMetricFill percent={licenseUsage.formSchemas.percentage || 0} $color="#232562" />
                    </LicenseMetricBar>
                    <LicenseMetricValue theme={theme}>
                      {licenseUsage.formSchemas.current} / {licenseUsage.formSchemas.limit}
                    </LicenseMetricValue>
                  </LicenseMetric>
                )}
                {licenseUsage.pdfTemplates && licenseUsage.pdfTemplates.limit !== null && (
                  <LicenseMetric>
                    <LicenseMetricLabel theme={theme}>Report Templates</LicenseMetricLabel>
                    <LicenseMetricBar theme={theme}>
                      <LicenseMetricFill percent={licenseUsage.pdfTemplates.percentage || 0} $color="#232562" />
                    </LicenseMetricBar>
                    <LicenseMetricValue theme={theme}>
                      {licenseUsage.pdfTemplates.current} / {licenseUsage.pdfTemplates.limit}
                    </LicenseMetricValue>
                  </LicenseMetric>
                )}
                {licenseUsage.storage && licenseUsage.storage.limitBytes !== null && (
                  <LicenseMetric>
                    <LicenseMetricLabel theme={theme}>Storage</LicenseMetricLabel>
                    <LicenseMetricBar theme={theme}>
                      <LicenseMetricFill percent={licenseUsage.storage.percentage || 0} $color="#232562" />
                    </LicenseMetricBar>
                    <LicenseMetricValue theme={theme}>
                      {licenseUsage.storage.currentFormatted} / {licenseUsage.storage.limitFormatted}
                    </LicenseMetricValue>
                  </LicenseMetric>
                )}
              </LicenseMetrics>
              {(() => {
                const nearLimit = [];
                const atLimit = [];
                const resources = [
                  { key: 'submissionsThisMonth', label: 'Submissions' },
                  { key: 'users', label: 'Users' },
                  { key: 'formSchemas', label: 'Forms' },
                  { key: 'pdfTemplates', label: 'Report Templates' },
                ];
                resources.forEach(r => {
                  const data = licenseUsage[r.key];
                  if (data && data.limit !== null && data.limit !== undefined) {
                    if (data.isAtLimit) atLimit.push(r.label);
                    else if (data.isNearLimit) nearLimit.push(r.label);
                  }
                });
                if (licenseUsage.storage && licenseUsage.storage.limitBytes !== null) {
                  if (licenseUsage.storage.isAtLimit) atLimit.push('Storage');
                  else if (licenseUsage.storage.isNearLimit) nearLimit.push('Storage');
                }
                if (atLimit.length > 0) {
                  return <LicenseWarning theme={theme} severe>Limit reached: {atLimit.join(', ')}</LicenseWarning>;
                }
                if (nearLimit.length > 0) {
                  return <LicenseWarning theme={theme}>Approaching limit: {nearLimit.join(', ')}</LicenseWarning>;
                }
                return null;
              })()}
            </LicenseCard>
          )}

          {/* Recent Submissions */}
          <ScrollablePanel theme={theme}>
            <PanelHeader theme={theme}>
              <PanelTitleGroup>
                <PanelAccent $color={theme.colors.tint} />
                <PanelTitle theme={theme}>Recent Submissions</PanelTitle>
              </PanelTitleGroup>
              <PanelLink theme={theme} onClick={() => navigate('/forms')}>View All</PanelLink>
            </PanelHeader>
            <ScrollablePanelBody noPadding theme={theme}>
              {loading ? (
                <LoadingText theme={theme}>Loading...</LoadingText>
              ) : recentSubmissions.length === 0 ? (
                <EmptyState theme={theme}>No recent submissions</EmptyState>
              ) : (
                recentSubmissions.slice(0, 10).map((sub, idx) => (
                  <SubItem key={sub.id || idx} theme={theme}>
                    <SubIcon theme={theme}><FileText size={16} color={theme.colors.tint} /></SubIcon>
                    <SubInfo>
                      <SubTitle theme={theme}>{sub.formName || 'Form Submission'}</SubTitle>
                      <SubMeta theme={theme}>
                        {sub.submitterName && <span>{sub.submitterName}</span>}
                        {sub.submittedAt && <span> &middot; {formatDateOnly(sub.submittedAt)}</span>}
                      </SubMeta>
                    </SubInfo>
                    {sub.status && (
                      <WOBadge color={sub.status === 'Submitted' ? StatusPalette.green : sub.status === 'Draft' ? StatusPalette.gray : StatusPalette.blue}>
                        {sub.status}
                      </WOBadge>
                    )}
                  </SubItem>
                ))
              )}
            </ScrollablePanelBody>
          </ScrollablePanel>
        </LeftStack>

        {/* Right column: Quick Actions */}
        <Panel theme={theme} style={{ display: 'flex', flexDirection: 'column' }}>
          <PanelHeader theme={theme}>
            <PanelTitleGroup>
              <PanelAccent $color={StatusPalette.purple} />
              <PanelTitle theme={theme}>Quick Access</PanelTitle>
            </PanelTitleGroup>
          </PanelHeader>
          <div style={{ padding: '0 24px 12px', fontSize: '15px', color: theme.colors.muted }}>
            Jump to frequently used features
          </div>
          <QuickActionsGrid>
            <QuickActionCard theme={theme} onClick={() => navigate('/form-builder')}>
              <QuickActionIcon $bg={theme.colors.tint + '15'}>
                <Wrench size={24} color={theme.colors.tint} />
              </QuickActionIcon>
              <QuickActionLabel>Create Form</QuickActionLabel>
              <QuickActionDesc theme={theme}>Build a new form template</QuickActionDesc>
            </QuickActionCard>

            <QuickActionCard theme={theme} onClick={() => navigate('/forms')}>
              <QuickActionIcon $bg={withOpacity(StatusPalette.green, '15')}>
                <FileStack size={24} color={StatusPalette.green} />
              </QuickActionIcon>
              <QuickActionLabel>View Forms</QuickActionLabel>
              <QuickActionDesc theme={theme}>Browse existing forms</QuickActionDesc>
            </QuickActionCard>

            <QuickActionCard theme={theme} onClick={() => navigate('/work-orders/new')}>
              <QuickActionIcon $bg={withOpacity(StatusPalette.amber, '15')}>
                <ClipboardCheck size={24} color={StatusPalette.amber} />
              </QuickActionIcon>
              <QuickActionLabel>New Work Order</QuickActionLabel>
              <QuickActionDesc theme={theme}>Create a work order</QuickActionDesc>
            </QuickActionCard>

            <QuickActionCard theme={theme} onClick={() => navigate('/calendar')}>
              <QuickActionIcon $bg={withOpacity(StatusPalette.purple, '15')}>
                <Calendar size={24} color={StatusPalette.purple} />
              </QuickActionIcon>
              <QuickActionLabel>Calendar</QuickActionLabel>
              <QuickActionDesc theme={theme}>View work order schedule</QuickActionDesc>
            </QuickActionCard>

            <QuickActionCard theme={theme} onClick={() => navigate('/customers')}>
              <QuickActionIcon $bg={withOpacity(StatusPalette.orange, '18')}>
                <User size={24} color={StatusPalette.orange} />
              </QuickActionIcon>
              <QuickActionLabel>Customers</QuickActionLabel>
              <QuickActionDesc theme={theme}>Manage customer records</QuickActionDesc>
            </QuickActionCard>

            <QuickActionCard theme={theme} onClick={() => navigate('/locations')}>
              <QuickActionIcon $bg={withOpacity(StatusPalette.blue, '15')}>
                <BarChart3 size={24} color={StatusPalette.blue} />
              </QuickActionIcon>
              <QuickActionLabel>Locations</QuickActionLabel>
              <QuickActionDesc theme={theme}>Manage site locations</QuickActionDesc>
            </QuickActionCard>
          </QuickActionsGrid>
          {(userTimezone || browserTimezone) && (
            <div style={{ padding: '0 24px 20px', display: 'flex', alignItems: 'center', gap: 8, fontSize: '15px', color: theme.colors.muted }}>
              <Globe size={18} />
              <span>Timezone: <strong style={{ color: theme.colors.text }}>{userTimezone || browserTimezone}</strong></span>
            </div>
          )}
        </Panel>
      </EnterpriseRow>

      {/* Submission Trends + Upcoming Work Orders — side by side */}
      <TwoColumnLayout theme={theme}>
        <Panel theme={theme}>
          <PanelHeader theme={theme}>
            <PanelTitleGroup>
              <PanelAccent $color={theme.colors.tint} />
              <PanelTitle theme={theme}>Submission Trends</PanelTitle>
            </PanelTitleGroup>
          </PanelHeader>
          <div style={{ padding: '0 24px 24px' }}>
            <SubmissionsChart data={analytics} loading={analyticsLoading} />
          </div>
        </Panel>

        <Panel theme={theme}>
          <PanelHeader theme={theme}>
            <PanelTitleGroup>
              <PanelAccent $color={StatusPalette.purple} />
              <PanelTitle theme={theme}>Upcoming Work Orders</PanelTitle>
            </PanelTitleGroup>
            <PanelLink theme={theme} onClick={() => navigate('/work-orders')}>View All</PanelLink>
          </PanelHeader>
          <PanelBody noPadding>
            {workOrdersLoading ? (
              <LoadingText theme={theme}>Loading...</LoadingText>
            ) : upcomingWorkOrders.length === 0 ? (
              <EmptyState theme={theme}>No upcoming work orders in the next 14 days</EmptyState>
            ) : (
              upcomingWorkOrders.slice(0, 6).map(wo => {
                const statusColor = STATUS_COLORS[wo.status] || StatusPalette.gray;
                return (
                  <WOItem
                    key={wo.id}
                    theme={theme}
                    onClick={() => navigate(`/work-orders/${wo.id}`)}
                  >
                    <WOStatusDot color={statusColor} />
                    <WOInfo>
                      <WOTitle theme={theme}>{wo.title}</WOTitle>
                      <WOMeta theme={theme}>
                        <span>{wo.workOrderNumber}</span>
                        {wo.customerName && <span>{wo.customerName}</span>}
                        <span>{wo.serviceDate ? formatDateOnly(wo.serviceDate) : wo.dueDate ? formatDateOnly(wo.dueDate) : ''}</span>
                      </WOMeta>
                    </WOInfo>
                    <WOBadge color={statusColor}>
                      {getWorkOrderStatusLabel(wo.status)}
                    </WOBadge>
                  </WOItem>
                );
              })
            )}
          </PanelBody>
        </Panel>
      </TwoColumnLayout>


    </Layout>
  );
};
